import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Modal, ProgressBar, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  fmCopy,
  fmDelete,
  fmDownloadUrl,
  fmList,
  fmMkdir,
  fmRename,
  fmUnzip,
  fmUpload,
  fmZip,
} from "../../redux/actionCreator";
import FileEditor from "./FileEditor";
import PermissionsModal from "./PermissionsModal";
import {
  Download, Package, X, ArrowUp, FolderOpen, Folder, FileArchive,
  FileText, File as FileIcon, Pencil, Eye, Radio, Copy as CopyIcon, Lock, Archive,
} from "lucide-react";
import "./fileManager.scss";

// Extensions opened in the editor by default (otherwise → download).
const TEXT_EXTS = [
  "txt", "md", "markdown", "log", "conf", "cfg", "ini", "env",
  "json", "yaml", "yml", "toml",
  "js", "jsx", "ts", "tsx", "mjs", "cjs",
  "css", "scss", "less", "html", "htm", "xml", "svg",
  "py", "rb", "php", "go", "rs", "java", "kt", "swift",
  "sh", "bash", "zsh", "fish",
  "sql", "csv", "tsv",
  "dockerfile", "gitignore", "lock",
];
function isTextFile(name) {
  const lower = name.toLowerCase();
  if (lower.startsWith(".") || lower === "dockerfile" || lower === "makefile") return true;
  const ext = (lower.split(".").pop() || "").toLowerCase();
  return TEXT_EXTS.includes(ext);
}

function joinPath(dir, name) {
  if (!dir || dir === "/") return "/" + name;
  return `${dir}/${name}`.replace(/\/+/g, "/");
}
function parentOf(p) {
  if (!p || p === "/") return "/";
  const idx = p.lastIndexOf("/");
  if (idx <= 0) return "/";
  return p.slice(0, idx);
}
function fmt(bytes) {
  if (bytes == null) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
}

export default function FileManager() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, serverId } = useParams();
  const [params] = useSearchParams();
  const label = params.get("label") || "File manager";

  const [path, setPath] = useState("/");
  const [pathInput, setPathInput] = useState("/"); // mirrors `path` but editable
  const [basePath, setBasePath] = useState("");
  const [entries, setEntries] = useState([]);
  // Sort: folders are always grouped first (cPanel convention); within each
  // group we order by the selected column. Default name asc matches the
  // backend's default ordering.
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [uploading, setUploading] = useState(null);
  const [editor, setEditor] = useState(null);  // { filePath, readOnly, tail }
  const [chmodFor, setChmodFor] = useState(null); // { filePath, fileName, mode }
  const [dragging, setDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    const res = await dispatch(fmList(projectId, serverId, path));
    setLoading(false);
    if (res?.status) {
      setEntries(res.data.entries || []);
      setBasePath(res.data.base_path || "");
      // server normalizes the path; sync ours.
      if (res.data.path && res.data.path !== path) setPath(res.data.path);
    } else {
      setError(res?.message || "Failed to load");
      setEntries([]);
    }
  }, [dispatch, projectId, serverId, path]);

  useEffect(() => {
    load();
  }, [load]);

  // Mirror the canonical `path` into the editable input whenever it changes
  // via breadcrumb / double-click / Up button.
  useEffect(() => {
    setPathInput(path);
  }, [path]);

  // Normalize "/x/y/" or "x/y" → "/x/y" before cd-ing.
  const normalize = (p) => {
    if (!p) return "/";
    let n = p.startsWith("/") ? p : `/${p}`;
    n = n.replace(/\/+/g, "/");
    if (n.length > 1 && n.endsWith("/")) n = n.slice(0, -1);
    return n;
  };
  const goToPath = () => {
    const next = normalize(pathInput);
    if (next !== path) setPath(next);
    else load(); // same path → force a refresh
  };

  const enter = (e) => {
    if (e.is_dir) setPath(joinPath(path, e.name));
  };
  const up = () => setPath(parentOf(path));

  const toggleSelect = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const selectAll = (checked) => {
    if (!checked) setSelected(new Set());
    else setSelected(new Set(entries.map((e) => e.name)));
  };

  const breadcrumbs = useMemo(() => {
    const segs = path.split("/").filter(Boolean);
    const out = [{ label: "/", path: "/" }];
    let acc = "";
    for (const s of segs) {
      acc = `${acc}/${s}`;
      out.push({ label: s, path: acc });
    }
    return out;
  }, [path]);

  // Apply the current sort. Folders always cluster at the top so navigation
  // stays predictable; size/mtime apply within each group.
  const sortedEntries = useMemo(() => {
    const arr = [...entries];
    arr.sort((a, b) => {
      if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1;
      let cmp = 0;
      if (sortKey === "name") {
        cmp = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      } else if (sortKey === "size") {
        cmp = (a.size || 0) - (b.size || 0);
      } else if (sortKey === "mtime") {
        cmp = new Date(a.mtime).getTime() - new Date(b.mtime).getTime();
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [entries, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Size/date default to descending — most useful first (biggest / newest).
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };
  const sortArrow = (key) => {
    if (sortKey !== key) return <span className="sort-arrow muted">⇅</span>;
    return <span className="sort-arrow active">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  /* Actions ---------------------------------------------------- */

  const mkdir = async () => {
    const name = window.prompt("New folder name:");
    if (!name) return;
    const res = await dispatch(fmMkdir(projectId, serverId, path, name));
    if (res?.status) load();
    else alert(res?.message || "Failed");
  };

  const onUploadClick = () => fileInputRef.current?.click();
  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading({ pct: 0 });
    const res = await dispatch(
      fmUpload(projectId, serverId, path, files, (pct) => setUploading({ pct }))
    );
    setUploading(null);
    if (res?.status) load();
    else alert(res?.message || "Upload failed");
  };

  const download = (e) => {
    const url = fmDownloadUrl(projectId, serverId, joinPath(path, e.name));
    // Cookie carries auth.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Bulk download: a single file goes straight to the download URL; anything
  // else (folders, or multiple items) gets zipped server-side first and then
  // downloaded. The zip lands in the current folder so the user can re-use or
  // delete it.
  const downloadSelected = async () => {
    const names = Array.from(selected);
    if (!names.length) return;
    const onlyOne = names.length === 1 ? entries.find((x) => x.name === names[0]) : null;
    if (onlyOne && !onlyOne.is_dir) {
      download(onlyOne);
      return;
    }
    const defaultName = names.length === 1
      ? `${names[0].replace(/[^a-zA-Z0-9._-]/g, "_")}.zip`
      : `download-${new Date().toISOString().slice(0, 10)}.zip`;
    const archive = window.prompt("Archive name for download:", defaultName);
    if (!archive) return;
    const res = await dispatch(fmZip(projectId, serverId, path, names, archive));
    if (!res?.status) {
      alert(res?.message || "Zip failed");
      return;
    }
    load();
    // Give the listing a tick to reflect the new file, then trigger download.
    setTimeout(() => {
      const url = fmDownloadUrl(projectId, serverId, joinPath(path, archive));
      window.open(url, "_blank", "noopener,noreferrer");
    }, 250);
  };

  // Single-folder download: zip just that folder + immediately download.
  const downloadFolder = async (e) => {
    const safeBase = e.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const defaultName = `${safeBase}.zip`;
    const archive = window.prompt(`Zip "${e.name}" as:`, defaultName);
    if (!archive) return;
    const res = await dispatch(fmZip(projectId, serverId, path, [e.name], archive));
    if (!res?.status) {
      alert(res?.message || "Zip failed");
      return;
    }
    load();
    setTimeout(() => {
      const url = fmDownloadUrl(projectId, serverId, joinPath(path, archive));
      window.open(url, "_blank", "noopener,noreferrer");
    }, 250);
  };

  const rename = async (e) => {
    const next = window.prompt(`Rename "${e.name}" to:`, e.name);
    if (!next || next === e.name) return;
    const from = joinPath(path, e.name);
    const to = joinPath(path, next);
    const res = await dispatch(fmRename(projectId, serverId, from, to));
    if (res?.status) load();
    else alert(res?.message || "Rename failed");
  };

  const remove = async (names) => {
    if (!names.length) return;
    const list = names.length === 1 ? `"${names[0]}"` : `${names.length} items`;
    if (!window.confirm(`Delete ${list}? This is permanent.`)) return;
    for (const n of names) {
      // eslint-disable-next-line no-await-in-loop
      const res = await dispatch(fmDelete(projectId, serverId, joinPath(path, n)));
      if (!res?.status) {
        alert(`Failed on "${n}": ${res?.message || "error"}`);
        break;
      }
    }
    load();
  };

  const zipSelected = async () => {
    if (!selected.size) return;
    const archive = window.prompt("Archive name:", "archive.zip");
    if (!archive) return;
    const res = await dispatch(
      fmZip(projectId, serverId, path, Array.from(selected), archive)
    );
    if (res?.status) load();
    else alert(res?.message || "Zip failed");
  };

  const unzip = async (e) => {
    if (!window.confirm(`Extract "${e.name}" into current folder?`)) return;
    const res = await dispatch(fmUnzip(projectId, serverId, path, e.name));
    if (res?.status) load();
    else alert(res?.message || "Unzip failed");
  };

  const openInEditor = (e, opts = {}) =>
    setEditor({
      filePath: joinPath(path, e.name),
      readOnly: !!opts.readOnly,
      tail: !!opts.tail,
    });

  const openPerms = (e) =>
    setChmodFor({
      filePath: joinPath(path, e.name),
      fileName: e.name,
      mode: e.mode || 0,
    });

  const moveOrCopy = async (e, kind) => {
    const dest = window.prompt(
      `Destination path (relative to ${basePath || "root"}; include the new filename):`,
      joinPath(path, e.name)
    );
    if (!dest || dest === joinPath(path, e.name)) return;
    const from = joinPath(path, e.name);
    const action = kind === "copy" ? fmCopy : fmRename;
    const res = await dispatch(action(projectId, serverId, from, dest));
    if (res?.status) load();
    else alert(res?.message || `${kind} failed`);
  };

  /* Drag-and-drop upload */
  const onDragEnter = (e) => {
    if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragging(false);
    }
  };
  const onDragOver = (e) => {
    if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
    e.preventDefault();
  };
  const onDrop = async (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    setUploading({ pct: 0 });
    const res = await dispatch(
      fmUpload(projectId, serverId, path, files, (pct) => setUploading({ pct }))
    );
    setUploading(null);
    if (res?.status) load();
    else alert(res?.message || "Upload failed");
  };

  /* Render ----------------------------------------------------- */

  return (
    <div
      className={`file-manager ${dragging ? "dragging" : ""}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dragging && (
        <div className="fm-drop-overlay">
          <div>
            <div style={{ fontSize: 48 }}><Download size={48} /></div>
            <div>Drop files to upload to <code>{path}</code></div>
          </div>
        </div>
      )}
      <div className="fm-bar">
        <Button
          size="sm"
          variant="light"
          onClick={() => navigate("/projects")}
          className="me-2"
        >
          ← Back
        </Button>
        <strong className="me-2">{label}</strong>
        <code className="root-chip">{basePath || "—"}</code>
        <div className="ms-auto d-flex gap-2">
          <Button size="sm" variant="light" onClick={load}>
            Refresh
          </Button>
          <Button size="sm" variant="light" onClick={mkdir}>
            + Folder
          </Button>
          <Button size="sm" variant="primary" onClick={onUploadClick}>
            ↑ Upload
          </Button>
          {selected.size > 0 && (
            <>
              <Button size="sm" variant="light" onClick={downloadSelected}>
                ↓ Download ({selected.size})
              </Button>
              <Button size="sm" variant="light" onClick={zipSelected}>
                <Package size={14} className="me-1" /> Zip ({selected.size})
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => remove(Array.from(selected))}
              >
                <X size={14} className="me-1" /> Delete ({selected.size})
              </Button>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={onFilesChosen}
        />
      </div>

      <div className="fm-crumbs">
        {path !== "/" && (
          <Button size="sm" variant="link" className="up" onClick={up}>
            <ArrowUp size={14} className="me-1" /> Up
          </Button>
        )}
        {breadcrumbs.map((c, i) => (
          <React.Fragment key={c.path}>
            {i > 0 && <span className="sep">/</span>}
            <button className="crumb" onClick={() => setPath(c.path)}>
              {c.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="fm-path-bar">
        <input
          className="form-control fm-path-input"
          value={pathInput}
          onChange={(e) => setPathInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") goToPath();
          }}
          placeholder="/path/inside/base"
          spellCheck={false}
          autoCorrect="off"
        />
        <Button
          size="sm"
          variant="primary"
          onClick={goToPath}
          disabled={!pathInput?.trim()}
        >
          Go
        </Button>
      </div>

      {uploading && (
        <div className="fm-uploading">
          <small>Uploading…</small>
          <ProgressBar now={uploading.pct} label={`${uploading.pct}%`} />
        </div>
      )}

      {error && <div className="alert alert-danger m-3">{error}</div>}

      {editor && (
        <FileEditor
          projectId={projectId}
          serverId={serverId}
          filePath={editor.filePath}
          readOnly={editor.readOnly}
          tail={editor.tail}
          onClose={() => setEditor(null)}
          onSaved={() => load()}
        />
      )}
      {chmodFor && (
        <PermissionsModal
          projectId={projectId}
          serverId={serverId}
          filePath={chmodFor.filePath}
          fileName={chmodFor.fileName.split("/").pop()}
          initialMode={chmodFor.mode}
          onClose={() => setChmodFor(null)}
          onApplied={() => load()}
        />
      )}

      <div className="fm-table-wrap">
        <table className="fm-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={
                    entries.length > 0 && selected.size === entries.length
                  }
                  onChange={(e) => selectAll(e.target.checked)}
                />
              </th>
              <th>
                <button
                  type="button"
                  className="sort-btn"
                  onClick={() => toggleSort("name")}
                  title="Sort by name"
                >
                  Name {sortArrow("name")}
                </button>
              </th>
              <th style={{ width: 110 }}>
                <button
                  type="button"
                  className="sort-btn"
                  onClick={() => toggleSort("size")}
                  title="Sort by size"
                >
                  Size {sortArrow("size")}
                </button>
              </th>
              <th style={{ width: 170 }}>
                <button
                  type="button"
                  className="sort-btn"
                  onClick={() => toggleSort("mtime")}
                  title="Sort by modified date"
                >
                  Modified {sortArrow("mtime")}
                </button>
              </th>
              <th className="text-end" style={{ width: 1 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="empty">
                  <Spinner size="sm" animation="border" /> Loading…
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty">
                  <div className="empty-state">
                    <div className="empty-art"><FolderOpen size={32} strokeWidth={1.2} /></div>
                    <h4>Empty folder</h4>
                    <p>Upload a file or create a new folder.</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedEntries.map((e) => {
                const isArchive = !e.is_dir && /\.(zip)$/i.test(e.name);
                const isText = !e.is_dir && isTextFile(e.name);
                return (
                  <tr key={e.name}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(e.name)}
                        onChange={() => toggleSelect(e.name)}
                      />
                    </td>
                    <td>
                      <button
                        className={`name-btn ${e.is_dir ? "dir" : "file"}`}
                        onClick={() => enter(e)}
                        title={
                          e.is_dir
                            ? "Open folder"
                            : isText
                            ? "Double-click to edit"
                            : "Double-click to download"
                        }
                        onDoubleClick={() => {
                          if (e.is_dir) return;
                          if (isText) openInEditor(e);
                          else download(e);
                        }}
                      >
                        <span className="icon">
                          {e.is_dir
                            ? <Folder size={14} />
                            : isArchive
                              ? <FileArchive size={14} />
                              : isText
                                ? <FileText size={14} />
                                : <FileIcon size={14} />}
                        </span>
                        {e.name}
                      </button>
                    </td>
                    <td>{e.is_dir ? "—" : fmt(e.size)}</td>
                    <td>{new Date(e.mtime).toLocaleString()}</td>
                    <td className="text-end">
                      {!e.is_dir && (
                        <Button
                          size="sm"
                          variant="light"
                          className="row-download-btn me-1"
                          onClick={() => download(e)}
                          title="Download this file"
                        >
                          ↓
                        </Button>
                      )}
                      {e.is_dir && (
                        <Button
                          size="sm"
                          variant="light"
                          className="row-download-btn me-1"
                          onClick={() => downloadFolder(e)}
                          title="Zip this folder and download"
                        >
                          ↓
                        </Button>
                      )}
                      <Dropdown align="end" style={{ display: "inline-block" }}>
                        <Dropdown.Toggle
                          size="sm"
                          variant="light"
                          className="row-actions-btn"
                        >
                          ⋯
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {!e.is_dir && isText && (
                            <>
                              <Dropdown.Item onClick={() => openInEditor(e)}>
                                <Pencil size={12} className="me-1" /> Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => openInEditor(e, { readOnly: true })}
                              >
                                <Eye size={12} className="me-1" /> View
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => openInEditor(e, { readOnly: true, tail: true })}
                              >
                                <Radio size={12} className="me-1" /> Tail (live)
                              </Dropdown.Item>
                              <Dropdown.Divider />
                            </>
                          )}
                          {!e.is_dir && (
                            <Dropdown.Item onClick={() => download(e)}>
                              ↓ Download
                            </Dropdown.Item>
                          )}
                          {e.is_dir && (
                            <Dropdown.Item onClick={() => downloadFolder(e)}>
                              ↓ Download as zip
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => rename(e)}>
                            <Pencil size={12} className="me-1" /> Rename
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => moveOrCopy(e, "move")}>
                            ↪ Move to…
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => moveOrCopy(e, "copy")}>
                            <CopyIcon size={12} className="me-1" /> Copy to…
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => openPerms(e)}>
                            <Lock size={12} className="me-1" /> Permissions
                          </Dropdown.Item>
                          {isArchive && (
                            <Dropdown.Item onClick={() => unzip(e)}>
                              <Archive size={12} className="me-1" /> Unzip here
                            </Dropdown.Item>
                          )}
                          <Dropdown.Divider />
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => remove([e.name])}
                          >
                            <X size={12} className="me-1" /> Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
